import { atom } from "jotai";
import { atomFamily, loadable } from "jotai/utils";
import { Agent } from "../types";

/* ------------------------------------------------------------------ */
/* Types / helpers                                                    */
/* ------------------------------------------------------------------ */
export interface AgentsListResponse {
  results: Agent[];
  meta: {
    limit: number;
    offset: number;
    count: number;
  };
  links: {
    first: string;
    last: string;
    prev?: string;
    next?: string;
  };
}

export interface ApiErrorResponse {
  errors: Array<{
    code: string;
    detail: string;
    status: string;
  }>;
}

/* ------------------------------------------------------------------ */
/* SUSPENDING atom family                                             */
/* ------------------------------------------------------------------ */

const agentsSearchAtomFamily_SUSPENDS = atomFamily((searchTerm: string) => {
  const baseAtom = atom<Promise<Agent[]>>(async () => {
    try {
      const query = searchTerm
        ? `?search=${encodeURIComponent(searchTerm)}`
        : "";
      const res = await fetch(`/api/canvasui/agents${query}`);

      if (!res.ok) {
        const data = (await res.json()) as ApiErrorResponse;
        // Handle v2 API error format: { errors: [{ code, detail, status }] }
        const errorMessage = data?.errors?.[0]?.detail ?? res.statusText;
        throw new Error(errorMessage);
      }

      const json = (await res.json()) as AgentsListResponse;
      return json.results as AgentsListResponse["results"];
    } catch (error) {
      console.error(
        `Error fetching Agents${
          searchTerm ? ` with "${searchTerm}" searchTerm` : ""
        }`,
        error,
      );
      throw error;
    }
  });

  baseAtom.debugLabel = `agentsSearchAtomFamily_SUSPENDS(searchTerm:${searchTerm})`;
  return baseAtom;
});

/* ------------------------------------------------------------------ */
/* NON-suspending (loadable) atom family                              */
/* ------------------------------------------------------------------ */

export const agentsSearchAtomFamily = atomFamily((searchTerm: string) => {
  const base = agentsSearchAtomFamily_SUSPENDS(searchTerm);
  return loadable(base);
});
